import { useState, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-form';
import { useTradesDataStore } from '@/stores';
import { useCreateTrade, usePrivateSettings, useCheckHoliday } from '@/hooks';
import {
  CreateTradeRequest,
  TradeType,
  SettlementVenue,
  BenchmarkRate,
  TRADE_REASON_LABELS,
  ALL_TRADE_TYPES,
  getAgreementType,
} from '@/types';
import { AvailabilityReportItem } from '@/types/availability.types';
import {
  calculateTradeValues,
  toFxRateString,
  TradeEntryFormData,
} from '@/services/trades/trade-service';
import { TRADE_TYPE_LABELS } from '../constants/trade-labels';
import { useTradeFormReferenceData } from './useTradeFormReferenceData';
import { useEscapeToClose, useModalAutoFocus } from './trade-form/useModalEffects';
import { useSecuritySearchDropdownEffects } from './trade-form/useSecuritySearchDropdownEffects';
import { useCounterpartyAccountSync, useCustodianAccountSync } from './trade-form/useAccountEffects';
import {
  missingAgreementMessage,
  repoCallCurrency,
  useRefetchCounterpartiesOnReturn,
} from './trade-form/counterparty-agreement';
import {
  useRateModeReset,
  useBenchmarkRateSync,
  useRepoRateModeGuard,
  useRepoCollateralGuard,
  useCashPoolRebateGuard,
  useCashPoolRateSync,
} from './trade-form/useRateSyncEffects';
import { useCashPools } from '@/hooks/cash-pool';
import { useCobFxPair } from '@/hooks/fx-rates';
import { cashPoolRate, poolsItsCash } from '@/utils/trades/cash-pool-rate';
import { randomUuid } from '@/utils/random-uuid';
import { getDefaultBenchmarkByCurrency } from './trade-form/benchmark-defaults';
import { useSelectedItemForTradePrefill, useInventoryDefaults, useCobGrossPricePrefill } from './trade-form/useInventoryPrefillEffects';
import { useCashMatchingFxWriteback } from './trade-form/useCashMatchingFxWriteback';
import { useTradeFormValidation } from './trade-form/useTradeFormValidationEffect';
import { CUSTODIAN_ACCOUNT_UNRESOLVED_MESSAGE } from './trade-form/custodian-account';
import { useInventorySearchIndex } from './trade-form/useInventorySearchIndex';
import { makeCreateTradeFormDefaults } from './trade-form/default-values';
import { useSecuritySelectionHandlers } from './trade-form/useSecuritySelectionHandlers';
import { useHiddenFieldErrorThaw } from './trade-form/useHiddenFieldErrorThaw';

// Re-exported from a leaf module so the extracted effect hooks can share it
// without a circular import; kept exported here to preserve the public API used
// by RateDetailsSection and the hook tests (LR-495).
export { getDefaultBenchmarkByCurrency };

// LR-718: the conditionally rendered fields whose validators must stop blocking
// submit once their section leaves the screen. Module-level so the identity is
// stable — `useHiddenFieldErrorThaw` takes them as an effect dependency.
const POOL_FIELDS = ['cash_pool'] as const;
const NON_CASH_COLLATERAL_FIELDS = ['collateral_isin', 'collateral_symbol_id', 'collateral_quantity'] as const;

export type TradeReasonValue = 'SPECIAL' | 'GENERAL_COLLATERAL' | 'CORPORATE_ACTION' | 'SHORT_COVERING' | 'MARKET_MAKING' | 'CLIENT_SERVICE' | 'INTERNAL' | 'OTHER';

// Dropdown order is explicit; labels come from the shared TRADE_REASON_LABELS
// map (single source of truth shared with the detail-page formatTradeReason).
export const TRADE_REASON_OPTIONS: Array<{ label: string; value: TradeReasonValue }> = (
  [
    'SPECIAL',
    'GENERAL_COLLATERAL',
    'CORPORATE_ACTION',
    'SHORT_COVERING',
    'MARKET_MAKING',
    'CLIENT_SERVICE',
    'INTERNAL',
    'OTHER',
  ] as TradeReasonValue[]
).map((value) => ({ value, label: TRADE_REASON_LABELS[value] ?? value }));

// Reasons that SURFACE the (optional, since LR-450 p.21) Reason Comment box.
// Not "required" any more — the comment is never a submit gate; this only
// decides which reasons benefit from a note and therefore show the field.
export const REASONS_WITH_COMMENT: TradeReasonValue[] = ['SPECIAL', 'CORPORATE_ACTION', 'OTHER'];

export type SearchInventoryItem = {
  legalEntity: string;
  accountId: string;
  counterparty: string;
  type: 'STOCK' | 'BOND' | 'ETF';
  sedol: string;
  isin: string;
  symbolId: string;
  shortName: string;
  price: number;
  currency: string;
  quantity: number;
  benchmarkFee: number;
};

export type SearchItem = {
  item: SearchInventoryItem;
  groupedItem: AvailabilityReportItem;
};

type SectionState = {
  securityDetails: boolean;
  accountDetails: boolean;
  positionDetails: boolean;
  nonCashCollateral: boolean;
  rateDetails: boolean;
  positionDates: boolean;
  settlementDetails: boolean;
  tradeSummary: boolean;
};

// `agreement` is absent on purpose (LR-634): it is derived from `trade_type` on
// the way out, so the form has no field for the counterparty registry to write.
export type TradeFormValues = Omit<CreateTradeRequest, 'fx_rate' | 'custodian_id' | 'agreement'> & {
  settlement_currency?: string;
  fx_rate?: number;
  net_settlement_converted_input?: number;
  trade_duration?: string;
  counterparty_id?: string;
  custodian_id?: string;
  agreement_id?: string;
  comments?: string;
  trade_time?: string;
};

// Suffix `.NNN` for custodian_account derived from venue (1=DTC, 2=Euroclear)
// and trade type (01=lend, 02=borrow, 03=repo, 04=reverse_repo).
const VENUE_DIGIT: Record<string, string> = { dtc: '1', euroclear: '2' };
const TRADE_TYPE_CODE: Record<TradeType, string> = {
  lend: '01',
  borrow: '02',
  repo: '03',
  reverse_repo: '04',
};
export const buildAccountSuffix = (tradeType: TradeType | undefined, venue: SettlementVenue | undefined): string => {
  if (!tradeType || !venue) return '';
  const v = VENUE_DIGIT[venue.toLowerCase()];
  const t = TRADE_TYPE_CODE[tradeType];
  if (!v || !t) return '';
  return `${v}${t}`;
};

export const combineDateAndTimeToIso = (dateStr: string, timeStr?: string): string => {
  if (!dateStr) return '';
  const time = timeStr && /^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)
    ? (timeStr.length === 5 ? `${timeStr}:00` : timeStr)
    : '00:00:00';
  // The trade time field is UTC (LR-809); the Z says so rather than leaving the
  // backend to read a zoneless time in its own default zone.
  return `${dateStr}T${time}Z`;
};

export function useCreateTradeForm() {
  const isOpen = useTradesDataStore((state) => state.isCreateTradeModalOpen);
  const setIsOpen = useTradesDataStore((state) => state.setIsCreateTradeModalOpen);
  const selectedItemForTrade = useTradesDataStore((state) => state.selectedItemForTrade);
  const setSelectedItemForTrade = useTradesDataStore((state) => state.setSelectedItemForTrade);
  const { mutate: createTrade, isPending: isCreating } = useCreateTrade();
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<SearchInventoryItem | null>(null);
  const [securitySearchQuery, setSecuritySearchQuery] = useState('');
  const [showSecurityDropdown, setShowSecurityDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const securityDropdownRef = useRef<HTMLDivElement>(null);
  const securityInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string; severity: 'error' | 'warning' }>>([]);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  // Lives as long as the form's values: every resend of one fill carries it, so a
  // resend after a timeout gets back the trade the first send booked instead of a
  // second one. Cleared wherever the values are (booked, or the form closed).
  const idempotencyKeyRef = useRef<string | null>(null);

  // Shared trade-form reference data: counterparties / custodians / benchmark
  // registry (LR-435/LR-443) / live FX, gated on `isOpen` (see
  // useTradeFormReferenceData). usePrivateSettings stays create-only — it feeds
  // legacy custodian-account prefixing.
  const {
    isUsingFallbackRates, counterparties, custodians, custodiansEmpty, custodiansError,
    counterpartiesEmpty, counterpartiesError, benchmarkRates, benchmarkMeta, refetchCounterparties,
  } = useTradeFormReferenceData(isOpen);
  const { data: privateSettings, isLoading: privateSettingsLoading } = usePrivateSettings();
  const [sectionStates, setSectionStates] = useState<SectionState>({
    securityDetails: true,
    accountDetails: true,
    positionDetails: true,
    nonCashCollateral: true,
    rateDetails: true,
    positionDates: true,
    settlementDetails: true,
    tradeSummary: true,
  });
  // LR-495: inventory search-index pipeline (filter-store subscription, fetch,
  // memo cascade) lives in its own hook. `allSLBPositions` feeds
  // handleSelectSecurity; `filteredInventoryItems` + `isDataLoading` are surfaced.
  const { allSLBPositions, filteredInventoryItems, isDataLoading } =
    useInventorySearchIndex(securitySearchQuery, isOpen);
  const defaultValues: TradeFormValues = makeCreateTradeFormDefaults();
  const pendingValidation = useRef<ReturnType<typeof setTimeout> | null>(null);
  const form = useForm({
    defaultValues,
    // LR-718: a refused Create must not be silent. A collapsed section UNMOUNTS its
    // fields, so an error armed while they were visible goes on blocking submit with
    // the offending field unreachable - and for the non-cash collateral fields
    // nothing names it at all, since the summary panel carries no rule for them (the
    // pool does have one, `useTradeFormValidationEffect`). Reopening every collapsed
    // section rather than just the guilty one is deliberate: a field -> section map
    // would be a parallel list of every validated field, and would drift on the first
    // one added.
    onSubmitInvalid: () => {
      setSectionStates((prev) => {
        if (Object.values(prev).every(Boolean)) return prev;
        const next = { ...prev };
        for (const key of Object.keys(next) as Array<keyof SectionState>) next[key] = true;
        return next;
      });
    },
    onSubmit: async ({ value }) => {
      // The debounced check of the last edit would otherwise land after the server's answer
      // and replace its errors in the panel.
      if (pendingValidation.current) clearTimeout(pendingValidation.current);
      setValidationErrors([]);

      // Guard: empty / out-of-range margin must not silently submit. Backend
      // accepts margin_bps in [100, 19900] (margin ∈ [1%, 199%]).
      if (value.margin_bps === undefined || value.margin_bps < 100 || value.margin_bps > 19900) {
        setValidationErrors([
          { field: 'margin_bps', message: 'Margin must be between 1% and 199%', severity: 'error' },
        ]);
        setIsSubmitted(true);
        return;
      }

      // LR-625: `custodian_account` is derived and read-only in the FORM (the
      // trader never types it); on the backend it is writable, required and
      // blank=False (PositionCreateSerializer Meta.fields, model CharField
      // without blank=True). With neither a custodian account_number nor an env
      // fallback the derivation yields '', and shipping it returned a raw DRF
      // "This field may not be blank" the trader could not act on.
      if (!value.custodian_account) {
        setValidationErrors([
          {
            field: 'custodian_account',
            message: CUSTODIAN_ACCOUNT_UNRESOLVED_MESSAGE,
            severity: 'error',
          },
        ]);
        setIsSubmitted(true);
        return;
      }

      // LR-617 / LR-844: repos are rebate-only and take Cash collateral only. The
      // form coerces both (useRepoRateModeGuard, useRepoCollateralGuard), but the
      // wire payload must be clean regardless of transient form state —
      // disable/hide alone does not block the action.
      const isRepoTrade = value.trade_type === 'repo' || value.trade_type === 'reverse_repo';

      // Transform form values to match CreateTradeRequest structure
      const requestData: CreateTradeRequest = {
        // Security Information
        symbol_id: value.symbol_id || selectedInventoryItem?.symbolId || '',
        description: value.description || selectedInventoryItem?.shortName || '',
        isin: value.isin || selectedInventoryItem?.isin || '',
        currency: value.currency || selectedInventoryItem?.currency || 'USD',

        // Account Information
        booking_center: value.booking_center || 'ALL',
        custodian_id: value.custodian_id ? Number(value.custodian_id) : undefined,
        custodian_account: value.custodian_account || '',
        counterparty_account: value.counterparty_account || '',
        counterparty_name: value.counterparty_name || '',

        // Agreement & Trade Type. LR-634: the trade's agreement follows its type,
        // never the counterparty's registry field - a counterparty may hold both,
        // and the API pairs `agreement` with `trade_type` (LR-617).
        agreement: getAgreementType(value.trade_type),
        trade_type: value.trade_type,
        collateral_type: isRepoTrade ? 'cash' : value.collateral_type,

        // Position Details
        quantity: value.quantity || 0,
        gross_price: typeof value.gross_price === 'string' ? value.gross_price : String(value.gross_price || 0),
        current_market_price: value.current_market_price || undefined,
        // value.margin_bps is guaranteed defined and in [100, 19900] by the guard above.
        margin_bps: value.margin_bps,

        // Rate Information
        rate_entry_mode: isRepoTrade ? 'rebate' : value.rate_entry_mode,
        rate_type: value.rate_type,
        benchmark_name: value.benchmark_name,
        // LR-631: a pooled fee trade's benchmark IS its pool's rate and the
        // server stamps it from the pool, so the form does not send one — the
        // field is hidden, and whatever it still holds is a leftover from before
        // the pool was picked. Posting that would either overwrite the pool's
        // figure on the paths where the server keeps the payload, or book a rate
        // nobody chose when the pool cannot be priced (where the server's own
        // 400 is the right answer).
        benchmark_rate:
          value.collateral_type === 'cash'
            && value.rate_entry_mode === 'fee'
            && value.cash_pool != null
            ? undefined
            : (value.benchmark_rate || '0.0000'),
        spread_bps: value.spread_bps,
        fee_percent: !isRepoTrade && value.fee_percent ? String(value.fee_percent) : undefined,
        day_count_convention: value.day_count_convention ?? null,

        // Trade Details
        trade_reason: value.trade_reason,
        trade_reason_comment: value.trade_reason_comment?.trim() || undefined,
        dividend_passthrough_percent: value.dividend_passthrough_percent || '100',

        // Duration
        term_type: value.term_type,
        trade_date: combineDateAndTimeToIso(value.trade_date, value.trade_time),
        settlement_date: value.settlement_date,
        // LR-684: Evergreen never submits a pre-set maturity_date, regardless
        // of what's still in form state - the field is hidden, but a stray
        // value there would otherwise hit the backend's create-time guard.
        maturity_date: value.term_type === 'evergreen' || value.term_type === 'open'
          ? null
          : (value.maturity_date || value.settlement_date),
        notice_period_days: value.term_type === 'evergreen' ? value.notice_period_days : null,

        // Settlement
        delivery_type: value.delivery_type,
        settlement_venue: value.settlement_venue,
        cash_settlement_place: value.cash_settlement_place ?? undefined,
        securities_settlement_place: value.securities_settlement_place ?? undefined,
        settlement_currency: value.settlement_currency || selectedInventoryItem?.currency || 'USD',
        fx_rate: toFxRateString(value.fx_rate),

        // LR-586 / LR-631: cash collateral names its pool, but only in the modes
        // that pool it — GMSLA fee and GMRA (repo books rebate-only, LR-617). A
        // GMSLA rebate trade sends no pool at all, and the API rejects one; the
        // key is omitted rather than sent as null wherever it does not apply,
        // exactly as on a non-cash trade.
        cash_pool:
          value.collateral_type === 'cash'
            && poolsItsCash(value.trade_type, value.rate_entry_mode)
            ? (value.cash_pool ?? null)
            : undefined,

        // Non-cash collateral — never on a repo (see isRepoTrade above).
        ...(isRepoTrade ? {} : {
          collateral_isin: value.collateral_isin?.trim() || undefined,
          collateral_symbol_id: value.collateral_symbol_id?.trim() || undefined,
          collateral_quantity: value.collateral_quantity ? String(value.collateral_quantity) : undefined,
          collateral_cob_price: value.collateral_cob_price ? String(value.collateral_cob_price) : undefined,
          collateral_current_market_price: value.collateral_current_market_price
            ? String(value.collateral_current_market_price)
            : undefined,
          collateral_percent: value.collateral_percent ? String(value.collateral_percent) : undefined,
          pre_delivery_party: value.pre_delivery_party || undefined,
          collateral_settlement_place: value.collateral_settlement_place ?? undefined,
        }),

        // Notes
        notes: value.comments || undefined,
      };

      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = randomUuid();
      createTrade({ data: requestData, idempotencyKey: idempotencyKeyRef.current }, {
        onSuccess: () => {
          idempotencyKeyRef.current = null;
          setIsOpen(false);
          form.reset();
          setValidationErrors([]);
          setSelectedInventoryItem(null);
          setSecuritySearchQuery('');
          setHasFormChanges(false);
          setIsSubmitted(false);
        },
        onError: (error: unknown) => {
          // Clear current errors before adding backend ones
          setValidationErrors([]);

          const backendErrors: Array<{ field: string; message: string; severity: 'error' | 'warning' }> = [];

          // Handle structured backend errors (e.g., { "field": ["error1", "error2"] })
          const errorData = (error as { response?: { data?: unknown } })?.response?.data;
          if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
            Object.entries(errorData).forEach(([field, messages]) => {
              const formattedField = field === 'non_field_errors' || field === 'detail'
                ? 'Server'
                : field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

              if (Array.isArray(messages)) {
                messages.forEach(msg => {
                  backendErrors.push({
                    field: field,
                    message: `${formattedField}: ${msg}`,
                    severity: 'error'
                  });
                });
              } else if (typeof messages === 'string') {
                backendErrors.push({
                  field: field,
                  message: `${formattedField}: ${messages}`,
                  severity: 'error'
                });
              }
            });
          }

          // If no structured errors found, use generic message
          if (backendErrors.length === 0) {
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
            backendErrors.push({
              field: 'backend',
              message: `Server error: ${errorMessage}`,
              severity: 'error'
            });
          }

          setValidationErrors(backendErrors);
          setIsSubmitted(true);
        },
      });
    },
  });
  const mapLegacyTradeType = useCallback((type: TradeType): TradeType => {
    const legacyMap: Record<string, TradeType> = {
      'STOCK_LEND_FEE': 'lend',
      'STOCK_LEND_REBATE': 'lend',
      'STOCK_BORROW_FEE': 'borrow',
      'STOCK_BORROW_REBATE': 'borrow',
      'CASH_REPO': 'repo',
      'STOCK_LENDING': 'lend',
      'STOCK_BORROWING': 'borrow',
      'REPO': 'repo',
      'REVERSE_REPO': 'reverse_repo',
    };
    return legacyMap[type] || type;
  }, []);
  const generateCustodianAccount = useCallback((tradeType: TradeType, settlementVenue: SettlementVenue): string => {
    if (!privateSettings) return '';

    const venueKey = settlementVenue.toUpperCase() as 'DTC' | 'EUROCLEAR';
    const venueSettings = privateSettings.exante_custodian_accounts[venueKey];

    if (!venueSettings) return '';

    const typeMapping: Record<TradeType, keyof typeof venueSettings> = {
      lend: 'lending',
      borrow: 'borrowing',
      repo: 'repo',
      reverse_repo: 'reverse_repo',
    };

    return venueSettings[typeMapping[tradeType]] || '';
  }, [privateSettings]);
  const quantity = useStore(form.store, (state: { values: TradeFormValues }) => state.values.quantity);
  const grossPrice = useStore(form.store, (state: { values: TradeFormValues }) => state.values.gross_price);
  const marginBps = useStore(form.store, (state: { values: TradeFormValues }) => state.values.margin_bps);
  const benchmarkRate = useStore(form.store, (state: { values: TradeFormValues }) => state.values.benchmark_rate);
  const rateType = useStore(form.store, (state: { values: TradeFormValues }) => state.values.rate_type);
  const benchmarkName = useStore(form.store, (state: { values: TradeFormValues }) => state.values.benchmark_name);
  const tradeDate = useStore(form.store, (state: { values: TradeFormValues }) => state.values.trade_date);
  const settlementDate = useStore(form.store, (state: { values: TradeFormValues }) => state.values.settlement_date);
  const maturityDate = useStore(form.store, (state: { values: TradeFormValues }) => state.values.maturity_date);
  const tradeDuration = useStore(form.store, (state: { values: TradeFormValues }) => state.values.trade_duration);
  const noticePeriodDays = useStore(form.store, (state: { values: TradeFormValues }) => state.values.notice_period_days);
  const settlementCurrency = useStore(form.store, (state: { values: TradeFormValues }) => state.values.settlement_currency);
  const fxRate = useStore(form.store, (state: { values: TradeFormValues }) => state.values.fx_rate);
  const netSettlementConvertedInput = useStore(form.store, (state: { values: TradeFormValues }) => state.values.net_settlement_converted_input);

  // Holiday checks for trade and settlement dates
  const { data: tradeDateHoliday } = useCheckHoliday(tradeDate, !!tradeDate);
  const { data: settlementDateHoliday } = useCheckHoliday(settlementDate, !!settlementDate);
  const tradeType = useStore(form.store, (state: { values: TradeFormValues }) => state.values.trade_type);

  const cashPool = useStore(form.store, (state: { values: TradeFormValues }) => state.values.cash_pool);
  const counterpartyId = useStore(form.store, (state: { values: TradeFormValues }) => state.values.counterparty_id);
  const counterpartyName = useStore(form.store, (state: { values: TradeFormValues }) => state.values.counterparty_name);
  const settlementVenue = useStore(form.store, (state: { values: TradeFormValues }) => state.values.settlement_venue);
  const custodianId = useStore(form.store, (state: { values: TradeFormValues }) => state.values.custodian_id);
  const deliveryType = useStore(form.store, (state: { values: TradeFormValues }) => state.values.delivery_type);
  const collateralType = useStore(form.store, (state: { values: TradeFormValues }) => state.values.collateral_type);
  const collateralQuantity = useStore(form.store, (state: { values: TradeFormValues }) => state.values.collateral_quantity);
  const collateralCurrentMarketPrice = useStore(form.store, (state: { values: TradeFormValues }) => state.values.collateral_current_market_price);
  const collateralPercent = useStore(form.store, (state: { values: TradeFormValues }) => state.values.collateral_percent);

  // Auto-update counterparty account from counterparty x trade type.
  useCounterpartyAccountSync({ form, counterpartyName, tradeType, counterparties });
  useRefetchCounterpartiesOnReturn(
    missingAgreementMessage(counterparties, counterpartyName, tradeType) !== null,
    refetchCounterparties,
  );
  // Asked of the server: the client's COB rates cover the majors only, and the pairs a repo
  // cannot be called in are exactly the others.
  const { data: repoCallRate } = useCobFxPair(
    selectedInventoryItem?.currency,
    repoCallCurrency(counterparties, counterpartyName, tradeType),
  );
  // Memoised: a fresh object per render would restart the debounced validation every render.
  const repoCallRateMissing = useMemo(
    () =>
      repoCallRate && repoCallRate.rate === null
        ? { from: repoCallRate.from_currency, to: repoCallRate.to_currency }
        : null,
    [repoCallRate],
  );
  const rateEntryMode = useStore(form.store, (state: { values: TradeFormValues }) => state.values.rate_entry_mode);

  // LR-631: a fee trade's cost leg accrues at its POOL's rate, so the preview
  // needs the pool itself, not just its id. Same filters AND the same gate as
  // `CashPoolField` - which is rendered under exactly this condition - so the
  // two share one cache entry instead of doubling the registry read, and a
  // booking that has no pool (GMSLA rebate, non-cash collateral) makes no
  // registry call at all.
  const poolProduct = tradeType ? getAgreementType(tradeType as TradeType) : undefined;
  const poolCounterparty = counterpartyId ? Number(counterpartyId) : undefined;
  const poolApplies = collateralType === 'cash' && poolsItsCash(tradeType, rateEntryMode);
  const { data: cashPools = [] } = useCashPools(
    {
      counterparty: poolCounterparty,
      product: poolProduct,
      in_agreement_currency: true,
      is_active: true,
    },
    {
      enabled: poolApplies && !!poolCounterparty && !!poolProduct,
      staleTime: 0,
    }
  );
  const selectedPool = useMemo(
    () => cashPools.find((pool) => pool.id === cashPool),
    [cashPools, cashPool]
  );
  const poolRate = useMemo(
    () => cashPoolRate(selectedPool, benchmarkRates),
    [selectedPool, benchmarkRates]
  );

  const feePercent = useStore(form.store, (state: { values: TradeFormValues }) => state.values.fee_percent);
  const spreadBps = useStore(form.store, (state: { values: TradeFormValues }) => state.values.spread_bps);
  const tradeReason = useStore(form.store, (state: { values: TradeFormValues }) => state.values.trade_reason);
  const dividendPassthroughPercent = useStore(form.store, (state: { values: TradeFormValues }) => state.values.dividend_passthrough_percent);
  const dayCountConvention = useStore(form.store, (state: { values: TradeFormValues }) => state.values.day_count_convention);

  const deferredQuantity = useDeferredValue(quantity);
  const deferredGrossPrice = useDeferredValue(grossPrice);
  const deferredMarginBps = useDeferredValue(marginBps);
  const deferredBenchmarkRate = useDeferredValue(benchmarkRate);
  const deferredFeePercent = useDeferredValue(feePercent);
  const deferredSpreadBps = useDeferredValue(spreadBps);

  const totalRate = useMemo(() => {
    // LR-464 sign convention:
    //   Fee mode:         total = fee − benchmark (positive when fee > benchmark)
    //   Rebate floating:  rebate = benchmark + spread/100 (special stocks carry a NEGATIVE spread)
    //   Rebate fixed:     equals the rebate rate the user typed into benchmark_rate
    // `|| 0` collapses NaN (intermediate states like '-' or '' during typing)
    // to 0 — otherwise totalRate displays "NaN%" mid-edit. Negative values
    // remain truthy and pass through unchanged.
    const benchmark = typeof deferredBenchmarkRate === 'string' ? (parseFloat(deferredBenchmarkRate) || 0) : (deferredBenchmarkRate || 0);

    if (rateEntryMode === 'fee') {
      const fee = typeof deferredFeePercent === 'string' ? parseFloat(deferredFeePercent) : (deferredFeePercent || 0);
      return fee - benchmark;
    } else {
      // rebate mode
      if (rateType === 'fixed') {
        return benchmark;
      } else {
        // floating
        const spread = typeof deferredSpreadBps === 'string' ? parseFloat(deferredSpreadBps) : (deferredSpreadBps || 0);
        return benchmark + (spread / 100);
      }
    }
  }, [rateEntryMode, rateType, deferredBenchmarkRate, deferredFeePercent, deferredSpreadBps]);

  const isFormDisabled = !selectedInventoryItem;
  const securityCurrency = selectedInventoryItem?.currency || 'USD';
  const effectiveSettlementCurrency = settlementCurrency || securityCurrency;
  const isCrossCurrency = effectiveSettlementCurrency !== securityCurrency;
  const isCashMatchingMode = !!netSettlementConvertedInput && netSettlementConvertedInput > 0;

  // Compute read-only `custodian_account` as `{base}.{suffix}`:
  //   base = selected Custodian.account_number, fallback = env-derived prefix
  //          extracted from privateSettings.exante_custodian_accounts (legacy).
  //   suffix = venue digit + trade type code (e.g. dtc + lend → "101").
  // LR-439: the value is fully determined by Custodian × Trade Type ×
  // Settlement Venue — the operator no longer edits the field, and there is no
  // venue→custodian auto-pick (the custodian is selected explicitly). The
  // effect below writes the result into the read-only form field.
  // LR-625: kept separate from the derivation below because it also answers
  // "is the Custodian select mandatory?" — without an env base the account can
  // only come from a picked custodian.
  const envFallbackBase = useMemo(() => {
    if (!tradeType || !settlementVenue) return '';
    const envFull = generateCustodianAccount(tradeType as TradeType, settlementVenue as SettlementVenue);
    return envFull.split('.')[0] || '';
  }, [tradeType, settlementVenue, generateCustodianAccount]);

  const computedCustodianAccount = useMemo(() => {
    const suffix = buildAccountSuffix(tradeType as TradeType, settlementVenue as SettlementVenue);
    const selected = custodianId
      ? custodians.find((c) => String(c.id) === String(custodianId))
      : undefined;

    const base = selected?.account_number?.trim() || envFallbackBase;

    if (!base) return '';
    return suffix ? `${base}.${suffix}` : base;
  }, [custodianId, custodians, tradeType, settlementVenue, envFallbackBase]);

  useCustodianAccountSync({ form, computedCustodianAccount });

  const custodianAccount = useStore(form.store, (state: { values: TradeFormValues }) => state.values.custodian_account);
  const calculatedValues = useMemo(() => {
    const parsedGrossPrice = typeof deferredGrossPrice === 'string' ? parseFloat(deferredGrossPrice) : deferredGrossPrice;
    const calculationData: Partial<TradeEntryFormData> = {
      security: {
        currency: securityCurrency,
      },
      position: {
        quantity: deferredQuantity || 0,
        contractPrice: parsedGrossPrice || 0,
        marginBps: deferredMarginBps || 0,
        availableQuantity: selectedInventoryItem?.quantity,
        // LR-367: needed for pnl_direction matrix in calculateTradeValues so
        // GMSLA lend daily_earnings flips sign correctly in UI.
        tradeType: tradeType as TradeType | undefined,
      },
      rate: {
        fixedRate: totalRate,
        // LR-365: wire mode/spread/fee/benchmark so calculateTradeValues can
        // render cost_of_collateral / fee_income / net_amount in Fee mode.
        rateEntryMode,
        benchmark: benchmarkName,
        benchmarkRate: benchmarkRates[benchmarkName as BenchmarkRate] ?? 0,
        spreadBps: typeof deferredSpreadBps === 'string'
          ? parseFloat(deferredSpreadBps) || 0
          : (deferredSpreadBps || 0),
        feePercent: typeof deferredFeePercent === 'string'
          ? parseFloat(deferredFeePercent) || 0
          : (deferredFeePercent || 0),
        // LR-415: thread day-count override into calc so daily/annual preview
        // mirrors what backend stores. Null → currency-driven default in dayCountFor.
        dayCountConvention: dayCountConvention ?? undefined,
        // LR-631: the cost leg of a fee trade prices on its pool's rate/basis
        // pair. Undefined until a pool is picked, and the calculator falls back
        // to the form benchmark then.
        poolRate: poolRate?.rate,
        poolDayCount: poolRate?.dayCount,
      },
      dates: {
        tradeDate: tradeDate,
        settlementDate: settlementDate,
        tradeDuration: tradeDuration as 'OPEN' | 'EVERGREEN' | 'TERM' | undefined,
        maturityDate: maturityDate ?? undefined,
        noticePeriodDays: noticePeriodDays ?? undefined,
      },
      settlement: {
        settlementCurrency: effectiveSettlementCurrency,
        fxRate: fxRate,
        netSettlementConvertedInput: netSettlementConvertedInput,
      },
    };
    const base = calculateTradeValues(calculationData);
    return {
      ...base,
      // *Security: security-currency figures (LR-459 renamed these from the
      // misleading *USD suffix — they were never USD-specific). The dead
      // `marginPriceUSD` (= marginPrice × qty = contractValue, unconsumed) was
      // dropped in the same pass.
      netSettlementSecurity: base.netSettlement,
      collateralRequiredSecurity: base.collateralRequired,
      expectedDailyReturnSecurity: base.expectedReturn,
      // *Settlement: settlement-currency equivalents for cross-currency trades
      // (undefined for same-ccy → the *Settlement cells stay hidden). The booked
      // figures the detail/blotter read come from the backend *_converted fields;
      // these mirror them for the live form preview. LR-459.
      marginPriceSettlement: base.marginPriceConverted,
      expectedDailyReturnSettlement: base.expectedDailyReturnConverted,
      contractValueSettlement: base.contractValueConverted,
    };
  }, [
    deferredQuantity,
    deferredGrossPrice,
    deferredMarginBps,
    maturityDate,
    tradeDate,
    settlementDate,
    tradeDuration,
    noticePeriodDays,
    selectedInventoryItem?.quantity,
    securityCurrency,
    effectiveSettlementCurrency,
    fxRate,
    netSettlementConvertedInput,
    totalRate,
    rateEntryMode,
    benchmarkName,
    benchmarkRates,
    deferredSpreadBps,
    deferredFeePercent,
    tradeType,
    dayCountConvention,
    poolRate,
  ]);
  // Rate-entry-mode switch + async benchmark_rate sync. Kept as two SEPARATE
  // effects (see useRateSyncEffects) so the registry load can re-fire the rate
  // sync on its own and the two-render benchmark_name → benchmark_rate cascade
  // is preserved.
  useRateModeReset({
    form, rateEntryMode, securityCurrency, hasCashPool: cashPool != null,
  });
  useBenchmarkRateSync({
    form, benchmarkName, benchmarkRates, rateEntryMode, rateType,
    hasCashPool: cashPool != null,
  });
  // LR-631: GMSLA fee pools its cash and GMSLA rebate does not, so the pool is
  // dropped on a switch to rebate and its rate drives the benchmark fields on a
  // switch to fee.
  useCashPoolRebateGuard({ form, poolApplies, cashPool, tradeType });
  // LR-718: and once it is dropped, the field the trade no longer uses must stop
  // blocking Create. Two groups, because the non-cash pair hides on the opposite
  // condition. Both gates are SHAPE-only: the hook re-runs each field's gated
  // validator, so it can only retract what the shape no longer justifies. A merely
  // collapsed section is `onSubmitInvalid` above.
  useHiddenFieldErrorThaw({ form, applies: poolApplies, fields: POOL_FIELDS });
  useHiddenFieldErrorThaw({
    form,
    applies: collateralType === 'non_cash',
    fields: NON_CASH_COLLATERAL_FIELDS,
  });
  useCashPoolRateSync({
    form,
    rateEntryMode,
    poolRate: poolRate?.rate,
    poolBenchmarkName: selectedPool?.benchmark_name ?? undefined,
  });
  // LR-617: repos are rebate-only — coerce a fee selection back to rebate
  // whenever trade_type lands on repo/reverse_repo.
  useRepoRateModeGuard({ form, tradeType, rateEntryMode });
  // LR-844: and they take Cash collateral only — same trigger.
  useRepoCollateralGuard({ form, tradeType, collateralType });
  useSecuritySearchDropdownEffects({
    showSecurityDropdown,
    securityInputRef,
    securityDropdownRef,
    setDropdownPosition,
    setShowSecurityDropdown,
  });
  // LR-495: security-dropdown selection handlers live in their own hook. The
  // settlement_venue double-write with useInventoryDefaults (below) is unchanged —
  // call ordering and effect timing are preserved.
  const { handleSelectSecurity, handleClearSecurity } = useSecuritySelectionHandlers({
    form,
    allSLBPositions,
    benchmarkRates,
    setSelectedInventoryItem,
    setSecuritySearchQuery,
    setShowSecurityDropdown,
    setHasFormChanges,
    setValidationErrors,
  });
  useSelectedItemForTradePrefill({
    form,
    selectedItemForTrade,
    setSelectedItemForTrade,
    benchmarkRates,
    setSelectedInventoryItem,
    setSecuritySearchQuery,
  });
  useModalAutoFocus(isOpen, selectedInventoryItem, securityInputRef, setHasFormChanges);
  useInventoryDefaults({ form, selectedInventoryItem });
  // LR-578: reads-wise this order is documentation, not a load-bearing
  // invariant — the untouched-guard baseline is derived from the ITEM
  // (snapshotGrossPrice(item.price), LR-621), not from the form value
  // useInventoryDefaults writes, and the fetch's .then runs only after the
  // commit's full effect flush.
  useCobGrossPricePrefill({ form, selectedInventoryItem });
  useCashMatchingFxWriteback({
    form,
    isCashMatchingMode,
    calculatedFxRate: calculatedValues.fxRate,
    fxRate,
  });
  useTradeFormValidation({
    form,
    collateralType,
    dividendPassthroughPercent,
    tradeReason,
    selectedInventoryItem,
    setValidationErrors,
    pendingValidation,
    counterparties,
    repoCallRateMissing,
  });
  const handleCloseWithoutConfirmation = useCallback(() => {
    idempotencyKeyRef.current = null;
    setIsOpen(false);
    form.reset();
    setSelectedInventoryItem(null);
    setSecuritySearchQuery('');
    setSelectedItemForTrade(null);
    setValidationErrors([]);
    setHasFormChanges(false);
    setShowCloseConfirmation(false);
    setIsSubmitted(false);
  }, [setIsOpen, form, setSelectedItemForTrade]);
  const handleClose = useCallback(() => {
    if (hasFormChanges) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseWithoutConfirmation();
    }
  }, [hasFormChanges, handleCloseWithoutConfirmation]);
  useEscapeToClose(isOpen, showCloseConfirmation, handleClose);
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not on the dropdown
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);
  const toggleSection = useCallback((section: keyof SectionState) => {
    setSectionStates((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);
  const getCountryFromSecurity = useCallback((item: SearchInventoryItem): string => {
    if (item.isin) {
      if (item.isin.startsWith('US')) return 'US';
      if (item.isin.startsWith('GB')) return 'GB';
      if (item.isin.startsWith('JP')) return 'JP';
      if (item.isin.startsWith('HK')) return 'HK';
      if (item.isin.startsWith('FR') || item.isin.startsWith('DE') || item.isin.startsWith('NL') || item.isin.startsWith('IT') || item.isin.startsWith('ES')) return 'EU';
    }
    if (item.currency === 'EUR') return 'EU';
    if (item.currency === 'GBP') return 'GB';
    if (item.currency === 'JPY') return 'JP';
    if (item.currency === 'HKD') return 'HK';
    return 'US';
  }, []);
  // LR-634: every leg is offered on every counterparty. One counterparty can
  // hold both master agreements, so narrowing by the registry field hid legs
  // that book perfectly well.
  const availableTradeTypes = ALL_TRADE_TYPES;

  return {
    // State
    isOpen,
    isCreating,
    selectedInventoryItem,
    securitySearchQuery,
    showSecurityDropdown,
    dropdownPosition,
    isSubmitted,
    validationErrors,
    showCloseConfirmation,
    hasFormChanges,
    sectionStates,
    isDataLoading,
    isFormDisabled,
    securityCurrency,
    effectiveSettlementCurrency,
    isCrossCurrency,
    isCashMatchingMode,
    isUsingFallbackRates,
    custodianAccount,

    // Refs
    securityDropdownRef,
    securityInputRef,

    // Data
    counterparties,
    custodians,
    custodiansEmpty,
    custodiansError,
    counterpartiesEmpty,
    counterpartiesError,
    // LR-625: the Custodian select is mandatory exactly when no env base exists,
    // since the account then has nowhere else to come from. Held back while
    // /settings/private/ is in flight — the base is unknown, not absent, and an
    // asterisk that appears and vanishes reads as a glitch. Submitting during
    // that window is still safe: the onSubmit guard sees the empty account.
    custodianRequired: !envFallbackBase && !privateSettingsLoading,
    benchmarkRates,
    benchmarkMeta,
    filteredInventoryItems,
    calculatedValues,
    totalRate,
    availableTradeTypes,
    tradeTypeLabels: TRADE_TYPE_LABELS,

    // Form
    form,

    // Form field values (from useStore)
    rateType,
    rateEntryMode,
    tradeDuration,
    tradeDate,
    settlementDate,
    tradeType,
    counterpartyName,
    deliveryType,
    settlementVenue,
    collateralType,
    collateralQuantity,
    collateralCurrentMarketPrice,
    collateralPercent,
    fxRate,

    // Holiday checks
    tradeDateHoliday,
    settlementDateHoliday,

    // Handlers
    handleSelectSecurity,
    handleClearSecurity,
    handleClose,
    handleCloseWithoutConfirmation,
    handleBackdropClick,
    toggleSection,
    setSecuritySearchQuery,
    setShowSecurityDropdown,
    setIsSubmitted,
    setShowCloseConfirmation,
    setHasFormChanges,

    // Utilities
    mapLegacyTradeType,
    getCountryFromSecurity,
  };
}
